<template>
    <div class="row">
        <div class="col-12">
            <div class="card">
                <div class="card-header bg-white">
                    <h4 class="card-title mb-0">{{ $t('label.listingX', [$t('module.meetingRoom')]) }}</h4>
                </div>
                <div class="card-body">
                    <div class="row mb-3">
                        <div class="col-md-3">
                            <b-form-input
                                v-model="filter"
                                type="search"
                                :placeholder="$t('label.search')"
                                autocomplete="off"
                                v-on:change="searchListing"
                            />
                        </div>
                    </div>
                    <div class="table-responsive mb-0">
                        <b-table
                            v-if="status.list && !loading"
                            :items="tableRows"
                            :fields="fields"
                            primary-key="uuid"
                            responsive="sm"
                            hover
                            tbody-tr-class="cursor-pointer"
                            @row-clicked="toggleRowDetails"
                        >
                            <template #cell(name)="data">
                                <div class="d-flex align-items-center justify-content-between">
                                    <div class="d-flex align-items-center">
                                        <img
                                            v-if="data.item.logoUrl"
                                            :src="data.item.logoUrl"
                                            :alt="data.item.name"
                                            class="mr-2"
                                            style="width: 30px; height: 30px; border-radius: 50%; object-fit: cover;"
                                        />
                                        <div v-else class="avatar-xs mr-2 flex-shrink-0">
                                            <span :class="['avatar-title rounded-circle font-size-14', `bg-soft-${avatarColor(data.item.name)}`, `text-${avatarColor(data.item.name)}`]">
                                                {{ data.item.name ? data.item.name.charAt(0).toUpperCase() : '-' }}
                                            </span>
                                        </div>
                                        <span class="font-weight-bold">{{ data.item.name || '--' }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #cell(room)="data">
                                <b-badge variant="info" class="mr-2">{{ data.item.meetingRooms.length }}</b-badge>
                            </template>
                            <template #cell(action)="data">
                                 <i :class="['mdi font-size-18 text-info', data.detailsShowing ? 'mdi-chevron-up' : 'mdi-chevron-down']"></i>
                            </template>
                            <template #row-details="row">
                                <div class="row fade-in">
                                    <MeetingRoomCard
                                        v-for="room in row.item.meetingRooms"
                                        :key="room.uuid"
                                        :details="room"
                                        @openForm="openForm"
                                    />
                                    <div v-if="row.item.meetingRooms.length == 0" class="col-12">
                                        <b-alert show variant="warning" class="text-center mb-0">
                                            <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                                        </b-alert>
                                    </div>
                                </div>
                            </template>
                        </b-table>
                        <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
                            <b-spinner variant="info" role="status" />
                        </div>
                        <b-alert v-if="tableRows.length == 0 && status.list && !loading" show variant="warning" class="text-center">
                            <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                        </b-alert>
                    </div>
                </div>
            </div>
        </div>
        <b-modal
            id="meeting-room-booking-form"
            :title="$t('label.createX', [$t('module.booking')])"
            @hidden="closeForm"
            hide-footer
            hide-header-close
            centered
            size="xl"
            no-close-on-backdrop
            :no-close-on-esc="!bookingStatus.save"
        >
            <MeetingRoomBookingForm
                v-if="meetingRooms.length"
                :meetingRooms="meetingRooms"
                @done="closeForm"
                @saved="$emit('saved')"
            />
        </b-modal>
    </div>
</template>
<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import { meetingRoomMethods } from "@/state/app";
import { MEETING_ROOM_BOOKING_MODULE } from "@/state/app/meetingRoomBooking.state";
import { MEETING_ROOM_MODULE } from "@/state/app/meetingRoom.state";
import MeetingRoomBookingForm from "@/views/pages/ihextar/meetingRoomBooking/meetingRoomBookingForm";
import MeetingRoomCard from "@/views/pages/ihextar/meetingRoomBooking/meetingRoomCard";

export default {
    components: { MeetingRoomCard, MeetingRoomBookingForm },
    async created() {
        await this.getMeetingRoomList(this.searchCriteria);
        this.loading = false;
    },
    data() {
        return {
            filter: "",
            meetingRooms: [],
            loading: true,
            searchCriteria: {
                searchArray: [],
            },
            fields: [
                { key: 'name', label: i18n.t('label.name'), sortable: false, class: 'text-nowrap' },
                { key: 'room', label: i18n.t('label.room'), sortable: false, class: 'text-nowrap' },
                { key: 'action', label: i18n.t('label.action'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    methods: {
        ...meetingRoomMethods,
        getMeetingRoomList(filter) {
            this.searchCriteria = filter;
            return this.getAllMeetingRoom({filter});
        },
        searchListing() {
            this.searchCriteria.searchArray = [];
            this.searchCriteria.searchArray.push(this.filter);
            this.getMeetingRoomList(this.searchCriteria);
        },
        openForm(room) {
            this.meetingRooms = [room];
            this.$nextTick(() => {
                this.$bvModal.show('meeting-room-booking-form');
            });
        },
        closeForm() {
            this.meetingRooms = [];
            this.$bvModal.hide('meeting-room-booking-form');
        },
        toggleRowDetails(item) {
            const shouldOpen = !item._showDetails;
            this.tableRows.forEach(row => {
                row._showDetails = false;
            });
            item._showDetails = shouldOpen;
        },
        avatarColor(item) {
            const colors = ['primary', 'success', 'danger', 'warning', 'info'];
            if (!item) return 'secondary';
            return colors[item.length % colors.length];
        },
    },
    computed: {
        ...mapState(MEETING_ROOM_MODULE, ['status']),
        ...mapState(MEETING_ROOM_BOOKING_MODULE, { bookingStatus: state => state.status }),
        tableRows() {
            return (this.$store.state.app.meetingRoom.list.data || []).map(item => ({
                ...item,
                _showDetails: false,
            }));
        },
    },
}
</script>
